/**
 * Cliente das rotas de plano.
 *
 * Como nos modelos, a gravação envia o plano inteiro — metadados e agenda
 * juntos —, e por isso a versão importa: duas telas abertas no mesmo plano não
 * podem apagar o trabalho uma da outra.
 *
 * **Publicar é diferente de salvar.** Salvar mexe no rascunho; publicar congela
 * o conteúdo de cada dia e entrega ao aluno. Depois disso o plano não volta a
 * ser editável — o caminho de correção é arquivar e publicar outro.
 */

import type { Prescription } from '@/features/templates/api';
import { api } from '@/lib/apiClient';

export type PlanStatus = 'draft' | 'published' | 'archived';

export interface ScheduledWorkout {
  workout_id: string;
  /** `YYYY-MM-DD` */
  scheduled_for: string;
  sequence: number;
  template_id: string | null;
  status: string;
  /** Nulo enquanto rascunho. Depois de publicado é o que o aluno lê. */
  prescription_snapshot: SnapshotContent | null;
}

/** O conteúdo congelado. O formato é do servidor — ver `domain/training/snapshot.py`. */
export interface SnapshotContent {
  snapshot_version: number;
  template_id: string;
  template_version: number;
  title: string;
  content_locale: string;
  modality: string;
  objective: string | null;
  level: string;
  estimated_minutes: number | null;
  items: Array<{
    position: number;
    block_label: string | null;
    exercise_id: string | null;
    title: string;
    instructions: string | null;
    // A mesma união discriminada dos modelos: o snapshot é cópia dela.
    prescription: Prescription;
  }>;
}

export interface Plan {
  plan_id: string;
  relationship_id: string;
  title: string;
  content_locale: string;
  start_date: string;
  end_date: string | null;
  status: PlanStatus;
  published_at: string | null;
  version: number;
  workouts: ScheduledWorkout[];
}

export interface PlanPage {
  items: Plan[];
  total: number;
}

export interface PlanDraft {
  title: string;
  start_date: string;
  end_date: string | null;
  workouts: Array<{ scheduled_for: string; sequence: number; template_id: string | null }>;
}

export const listPlans = (relationshipId: string) =>
  api.get<PlanPage>(`/plans?relationship_id=${relationshipId}&limit=100`);

export const getPlan = (planId: string) => api.get<Plan>(`/plans/${planId}`);

export const createPlan = (relationshipId: string, draft: PlanDraft) =>
  api.post<Plan>('/plans', { ...draft, relationship_id: relationshipId });

export const savePlan = (planId: string, draft: PlanDraft, version: number) =>
  api.put<Plan>(`/plans/${planId}`, { ...draft, version });

export const publishPlan = (planId: string, version: number) =>
  api.post<Plan>(`/plans/${planId}/publish`, { version });

export const archivePlan = (planId: string, version: number) =>
  api.post<Plan>(`/plans/${planId}/archive`, { version });
